import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of, switchMap } from 'rxjs';
import { IGNORE_AUTH_ERRORS } from '../network-interceptor';
import { PaginationService } from './pagination.service';
import { SentryService } from './sentry.service';

@Injectable({
  providedIn: 'root',
})
export class GetAllService {
  private http = inject(HttpClient);
  private sentryService = inject(SentryService);
  private paginationService = inject(PaginationService);

  private urlCacheMap = new Map<string, unknown[]>();

  private reportError(context: HttpContext, err: Error) {
    if (!(err instanceof HttpErrorResponse)) return true;
    if ((err.status === 401 || err.status === 403) && context?.get(IGNORE_AUTH_ERRORS)) return false;
    return true;
  }

  public getAll<T>(
    url: string,
    context?: HttpContext,
    limit = 50,
    dontRecheckOld = false,
    page = 0,
    results: T[] = []
  ): Observable<T[]> {
    if (dontRecheckOld && !results.length) {
      const cachedResults = this.urlCacheMap.get(url);
      if (cachedResults) {
        page = Math.floor(cachedResults.length / limit);
        results = (cachedResults as T[]).slice(0, page * limit);
      }
    }

    const separator = url.includes('?') ? '&' : '?';
    const pageAfterItem = results.length ? (results[results.length - 1] as Record<string, unknown>) : undefined;
    const pageAfter = pageAfterItem ? this.paginationService.encodePageInfo(pageAfterItem, []) : undefined;
    const fullUrl = `${url}${separator}limit=${limit}&page=${page}${pageAfter ? `&page_after=${pageAfter}` : ''}`;

    return this.http
      .get<{ data: T[]; has_more?: boolean }>(fullUrl, { context })
      .pipe(
        catchError(err => {
          if (this.reportError(context, err)) {
            this.sentryService.captureException(`${err.message} - ${err.error?.message}`);
          }
          return of({ data: <T[]>[], has_more: false });
        })
      )
      .pipe(
        map(({ data, has_more }) => {
          return {
            data,
            has_more: has_more ?? data.length === limit,
          };
        }),
        switchMap(({ data, has_more }) => {
          results = [...results, ...data];
          if (!has_more) {
            if (dontRecheckOld) this.urlCacheMap.set(url, results);
            return of(results);
          }
          return this.getAll(url, context, limit, dontRecheckOld, page + 1, results);
        })
      );
  }

  public getAllStripe<T>(url: string, context?: HttpContext, limit = 50, page = 0, results: T[] = []): Observable<T[]> {
    const separator = url.includes('?') ? '&' : '?';
    const fullUrl = `${url}${separator}limit=${limit}&page=${page}`;

    return this.http
      .get<{ data: T[]; has_more?: boolean }>(fullUrl, { context })
      .pipe(
        catchError(err => {
          if (this.reportError(context, err)) {
            this.sentryService.captureException(`${err.message} - ${err.error?.message}`);
          }
          return of({ data: <T[]>[], has_more: false });
        })
      )
      .pipe(
        map(({ data, has_more }) => {
          return {
            data,
            has_more: has_more ?? data.length === limit,
          };
        }),
        switchMap(({ data, has_more }) => {
          results = [...results, ...data];
          if (!has_more) return of(results);

          if (url.includes('starting_after')) {
            url = url.replace(/starting_after=[^&]+/, `starting_after=${data[data.length - 1]['id']}`);
          } else {
            url += `${url.includes('?') ? '&' : '?'}starting_after=${data[data.length - 1]['id']}`;
          }

          return this.getAllStripe(url, context, limit, page + 1, results);
        })
      );
  }
}
